import React, { useState } from "react";
import { CalendarClock, Loader2, MessageSquareText } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { jobsApi } from "../lib/api";

function fmtWhen(iso) {
  if (!iso) return "the new time";
  const d = new Date(iso.length === 16 ? `${iso}:00` : iso);
  if (Number.isNaN(d.getTime())) return "the new time";
  const date = d.toLocaleDateString([], { weekday: "long", month: "short", day: "numeric" });
  const time = d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  return `${date} at ${time}`;
}

/** "Send updated time to the customer?" popup shown after a scheduled job's start changes. */
export default function RescheduleTextPopup({ job, onClose }) {
  const [sending, setSending] = useState(false);
  if (!job) return null;

  const send = async () => {
    setSending(true);
    try {
      const res = await jobsApi.sendReschedule(job.id);
      toast.success(`Updated time texted to ${res.to || "the customer"}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Text could not be sent");
    } finally {
      setSending(false);
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 z-[110] flex items-center justify-center bg-slate-900/50 backdrop-blur-[2px] p-4"
      data-testid="reschedule-text-popup"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="bg-white rounded-xl shadow-2xl border border-slate-200 w-full max-w-sm p-5 text-center">
        <div className="mx-auto h-10 w-10 rounded-full bg-amber-50 flex items-center justify-center mb-3">
          <CalendarClock className="h-5 w-5 text-amber-600" />
        </div>
        <div className="text-[15px] font-semibold text-slate-900">Send updated time to the customer?</div>
        <p className="text-[12.5px] text-slate-500 mt-1">
          {job.customer || "The customer"} will get a text
          {job.phone ? (
            <> at <span className="font-semibold text-slate-700">{job.phone}</span></>
          ) : (
            " at their number on file"
          )}{" "}
          with the new time for job {job.id}:
        </p>
        <div
          className="mt-2 text-[13px] font-bold text-slate-800 bg-slate-50 border border-slate-100 rounded-lg px-3 py-2"
          data-testid="reschedule-new-time"
        >
          {fmtWhen(job.start)}
        </div>
        <div className="flex items-center justify-center gap-2 mt-4">
          <Button
            type="button"
            variant="outline"
            data-testid="reschedule-text-skip"
            disabled={sending}
            onClick={onClose}
            className="h-9 text-[13px]"
          >
            No thanks
          </Button>
          <Button
            type="button"
            data-testid="reschedule-text-send"
            disabled={sending}
            onClick={send}
            className="h-9 bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-[13px] font-semibold"
          >
            {sending ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <MessageSquareText className="h-4 w-4 mr-1.5" />}
            Send text
          </Button>
        </div>
      </div>
    </div>
  );
}
